import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { Upload, Download, Trash2, Plus, Loader2, ShieldCheck } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  missing: { label: "Not uploaded", cls: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300" },
  no_date: { label: "No expiry date", cls: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300" },
  expired: { label: "Expired", cls: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300" },
  expiring: { label: "Expiring soon", cls: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300" },
  ok: { label: "Valid", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" },
};

export const LicensesPanel = ({ propertyId, user, initialUploadType }) => {
  const isAdmin = can(user, ["admin"]);
  const canUpload = can(user, ["admin", "regional", "finance", "gm"]);
  const [rows, setRows] = useState(null);
  const [newType, setNewType] = useState("");
  const [upload, setUpload] = useState(null); // {type, document_id}
  const [upFile, setUpFile] = useState(null);
  const [upExp, setUpExp] = useState("");
  const [upNotes, setUpNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const deepDone = useRef(false);

  const load = useCallback(() => {
    if (!propertyId) return;
    api.get(`/properties/${propertyId}/licenses`).then((r) => setRows(r.data.licenses)).catch(() => setRows([]));
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (type, enabled) => {
    try {
      const r = await api.patch(`/properties/${propertyId}/licenses`, { name: type, enabled });
      setRows(r.data.licenses);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const addType = async () => {
    const name = newType.trim();
    if (!name) return;
    try { await api.post("/license-types", { name }); setNewType(""); load(); toast.success("License type added"); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  const deleteType = async (name) => {
    if (!window.confirm(`Remove "${name}" from the license list? Uploaded files are kept.`)) return;
    try { await api.delete("/license-types", { params: { name } }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const openUpload = useCallback((arg) => {
    const row = typeof arg === "string" ? (rows || []).find((r) => r.type === arg) || { type: arg } : arg;
    setUpload({ type: row.type, document_id: row.document_id });
    setUpFile(null);
    setUpExp(row.expiration_date ? String(row.expiration_date).slice(0, 10) : "");
    setUpNotes(row.notes || "");
  }, [rows]);
  // Deep-link from the dashboard alert: auto-open the upload dialog once rows are loaded.
  useEffect(() => {
    if (initialUploadType && rows && !deepDone.current) { deepDone.current = true; openUpload(initialUploadType); }
  }, [initialUploadType, rows, openUpload]);
  const submitUpload = async () => {
    setSaving(true);
    try {
      if (upFile) {
        const fd = new FormData();
        fd.append("file", upFile);
        const params = new URLSearchParams({ property_id: propertyId, folder: "Licenses & Permits", title: upload.type, license_type: upload.type, expiration_date: upExp || "", notes: upNotes || "" });
        await api.post(`/documents?${params.toString()}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else if (upload.document_id) {
        await api.patch(`/documents/${upload.document_id}/license-meta`, { expiration_date: upExp || "", notes: upNotes || "" });
      } else {
        setSaving(false);
        return toast.error("Choose a file to upload");
      }
      toast.success(`${upload.type} saved`);
      setUpload(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setSaving(false); }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Delete this license document?")) return;
    try { await api.delete(`/documents/${id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const download = (id) => window.open(`${API_BASE}/api/documents/${id}/file?download=1`, "_blank");

  if (rows === null) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const enabledCount = rows.filter((r) => r.enabled).length;
  const issueCount = rows.filter((r) => r.enabled && ["missing", "no_date", "expired", "expiring"].includes(r.status)).length;

  return (
    <div className="space-y-4" data-testid="licenses-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg font-bold tracking-tight flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Licenses &amp; Permits</h3>
          <p className="text-sm text-muted-foreground">{enabledCount} tracked · {issueCount > 0 ? <span className="font-semibold text-red-600">{issueCount} need attention</span> : "all current"}. {isAdmin ? "Turn on the ones that apply to this property; GMs upload the file + expiration date." : "Upload each required license file and its expiration date."}</p>
        </div>
      </div>

      <Card className="rounded-none border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-left">
              {isAdmin && <th className="w-12 p-3">Track</th>}
              <th className="p-3">License / Permit</th>
              <th className="p-3">Status</th>
              <th className="p-3">Expiration</th>
              <th className="p-3">File</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const st = STATUS[r.status] || STATUS.ok;
              const dim = isAdmin && !r.enabled;
              return (
                <tr key={r.type} className={`border-b ${dim ? "opacity-45" : ""}`} data-testid={`license-row-${r.type}`}>
                  {isAdmin && (
                    <td className="p-3">
                      <Checkbox checked={r.enabled} onCheckedChange={(v) => toggle(r.type, !!v)} data-testid={`license-toggle-${r.type}`} />
                    </td>
                  )}
                  <td className="p-3 font-medium">
                    {r.type}
                    {r.notes && <div className="mt-0.5 text-xs font-normal text-muted-foreground" data-testid={`license-note-${r.type}`}>{r.notes}</div>}
                  </td>
                  <td className="p-3">
                    {r.enabled ? (
                      <Badge className={`rounded-none ${st.cls}`} data-testid={`license-status-${r.type}`}>
                        {r.status === "expiring" && r.days_to_expiry != null ? `Expires in ${r.days_to_expiry}d` : st.label}
                      </Badge>
                    ) : <span className="text-xs text-muted-foreground">Not tracked</span>}
                  </td>
                  <td className="p-3">{r.expiration_date ? fmtDate(r.expiration_date) : <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-3">
                    {r.document_id ? (
                      <button onClick={() => download(r.document_id)} className="flex items-center gap-1 text-primary hover:underline" data-testid={`license-download-${r.type}`}>
                        <Download className="h-3.5 w-3.5" /> {r.filename || "View"}
                      </button>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      {r.enabled && canUpload && (
                        <Button size="sm" variant="outline" onClick={() => openUpload(r)} data-testid={`license-upload-${r.type}`}>
                          <Upload className="mr-1 h-3.5 w-3.5" /> {r.document_id ? "Edit" : "Upload"}
                        </Button>
                      )}
                      {r.document_id && isAdmin && (
                        <Button size="icon" variant="ghost" onClick={() => deleteDoc(r.document_id)} data-testid={`license-deletedoc-${r.type}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      )}
                      {isAdmin && (
                        <Button size="icon" variant="ghost" onClick={() => deleteType(r.type)} title="Remove license type" data-testid={`license-deletetype-${r.type}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {isAdmin && (
        <div className="flex items-center gap-2">
          <Input value={newType} onChange={(e) => setNewType(e.target.value)} placeholder="Add a custom license/permit type" className="max-w-sm" data-testid="license-newtype" />
          <Button variant="outline" onClick={addType} data-testid="license-addtype-btn"><Plus className="mr-1 h-4 w-4" /> Add type</Button>
        </div>
      )}

      <Dialog open={!!upload} onOpenChange={(o) => { if (!o) setUpload(null); }}>
        <DialogContent data-testid="license-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Upload {upload?.type}</DialogTitle>
            <DialogDescription>Attach the document and set its expiration date so we can alert you before it lapses.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Document file {upload?.document_id && <span className="font-normal text-muted-foreground">(leave empty to keep the current file)</span>}</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" onChange={(e) => setUpFile(e.target.files?.[0] || null)} data-testid="license-file-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Expiration date</Label>
              <Input type="date" value={upExp} onChange={(e) => setUpExp(e.target.value)} data-testid="license-exp-input" />
              <p className="text-xs text-muted-foreground">Required for expiration alerts.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={upNotes} onChange={(e) => setUpNotes(e.target.value)} rows={2} placeholder="e.g. Renewal ref #12345 · Issued by City Fire Marshal" data-testid="license-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpload(null)}>Cancel</Button>
            <Button onClick={submitUpload} disabled={saving} data-testid="license-upload-submit">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
